import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import SAVE_VISITA_FONOAUDIOLOGO, { adaptAddVisitaDisfagiaFonoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaDisfagiaFonoaudiologo";
import GET_COMBO_VISITA_VALOR, { adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import { validateStepFields } from "../../../../utils/visitaStepValidation";

const COMBO_VISITA_TIPO_DIETA = 'TipoDieta';


export default function ProgramaDisfagiaFono({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, combosVisita }) {
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();

  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [comboTipoDietaOptions, setComboTipoDietaOptions] = useState([]);

  const inputCombos = groups.flatMap((g) => g.inputs.filter((i) => !!i.comboName));

  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
		if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
	}, [dataCombosRI]);

  const [loadComboTipoDietaOptions, { data: dataTipoDieta }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
		if (!dataTipoDieta) return;
    setComboTipoDietaOptions(adaptCombosVisitas(dataTipoDieta));
	}, [dataTipoDieta]);  

  const [save] = useMutation(SAVE_VISITA_FONOAUDIOLOGO);

  const formattedGroups = useMemo(() => {
    // IDs de dentición "Total" / "Parcial" desde combosRI
    const dOpts = combosRI?.idComboRIValorDenticion || [];
    const denticionTotalId = dOpts.find((o) => o.displayName?.toLowerCase().includes("total"))?.id;
    const denticionParcialId = dOpts.find((o) => o.displayName?.toLowerCase().includes("parcial"))?.id;
    const requiereProtesis = [denticionTotalId, denticionParcialId].includes(formData?.idComboRIValorDenticion);

    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        switch (input.name) {
          case "informeVideodeglucion":
            newInput.hidden = formData?.videodeglucion !== true;
            newInput.required = formData?.videodeglucion === true;
            break;

          case "idComboRIValorProtesis":
            newInput.hidden = !requiereProtesis;
            newInput.required = requiereProtesis;
            break;

          case "justificaNadaPorBoca":
            newInput.hidden = formData?.nadaPorBoca !== true;
            newInput.required = formData?.nadaPorBoca === true;
            break;

          case 'idComboVisitaValorTipoDieta':
            newInput.options = comboTipoDietaOptions;
            break;

          case "complicaciones":
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) return { ...opt, disabled: opt.id !== ningunaId };
                if (hasOther) return { ...opt, disabled: opt.id === ningunaId };
              }
              return { ...opt, disabled: false };
            });
            break;

          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, combosRI, formData, complicacionesCombo, comboTipoDietaOptions]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({ idArchivo: file.idArchivo, name, handleChange });
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({ variables: { input: adaptAddVisitaDisfagiaFonoInput(formData) } });

    const idVisitaDisfagia = data?.saveVisitaDisfagiaFonoaudiologo?.idVisitaDisfagia;
    if (!idVisitaDisfagia) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaDisfagia }));
  });

  useEffect(() => {
    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const tipoDietaId = combosVisita.find((c) => c.comboName === COMBO_VISITA_TIPO_DIETA)?.id;

    if (Number.isInteger(tipoDietaId)) {
      loadComboTipoDietaOptions({ variables: { idComboVisita: tipoDietaId } });
    }
  }, [combosVisita, loadComboTipoDietaOptions]);

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisita: visita.idVisita,
        idVisitaDisfagia: visita.idVisitaDisfagia
      });
    }
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaDisfagiaFono",
      stepName: "Disfagia (Fonoaudiólogo)",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
            {formattedGroups.
      filter((group) => !group.hidden).
      map((group, idx) =>
      <CardBox key={idx} title={group.title}>
                        <Grid container spacing={2}>
                            {group.inputs.
          filter((input) => !input.hidden).
          map((input) =>
          <Grid size={{ xs: 12 }} key={input.name}>
                                        <RenderInputByTypeMobile
              input={input}
              value={formData[input.name]}
              handleChange={input.type === "file" ? handleChangeFile : handleChange}
              handleDelete={input.type === "file" ? onDeleteImage : null}
              row={input.type === "multiple_boolean"} />
            
                                    </Grid>
          )}
                        </Grid>
                    </CardBox>
      )}
        </>);

}

const groups = [
{
  title: "",
  inputs: [
  {
    name: "videodeglucion",
    displayName: "Videodeglución",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "informeVideodeglucion",
    displayName: "Informe de Videodeglución",
    type: "file"
  },
  {
    name: "actividadMotora",
    displayName: "Actividad motora y postura corporal al momento de alimentarse",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "controlCefalico",
    displayName: "Control cefálico",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "idComboRIValorPostural",
    displayName: "Control postural",
    comboName: "Control postural",
    type: "select",
    required: true
  },
  {
    name: "idComboRIValorDenticion",
    displayName: "Dentición",
    comboName: 'Dentición',
    type: "select",
    required: true
  },
  {
    name: "idComboRIValorProtesis",
    displayName: "Prótesis",
    comboName: 'Prótesis',
    type: "select"
  }]

},
{
  title: "Indicaciones",
  inputs: [
  {
    name: "nadaPorBoca",
    displayName: "Nada por boca",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "justificaNadaPorBoca",
    displayName: "Justificar si es SI",
    type: "long_string",
    maxLength: 4000
  },
  {
    name: "idComboVisitaValorTipoDieta",
    displayName: "Tipo de dieta",
    type: "select",
    required: true
  },
  {
    name: "idComboRIValorHidratacion",
    displayName: "Hidratación",
    comboName: 'INDICACIONES LIQUIDOS',
    type: "select",
    required: true
  },
  {
    name: "complicaciones",
    displayName: "Complicaciones",
    type: "multiple_select",
    required: true
  }]

}];